import { useCallback, useMemo } from 'react'

import { useNavigate } from 'react-router'
import { usePermission, useSelectedTrial } from 'src/features/auth/context'
import { useApiClientFetch } from 'src/hooks/use-api-client-fetch'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'

import { getAddIcfDocumentRoute, getEditIcfDocumentRoute } from './../../routes'
import { useIcfDocumentHospital } from './../../use-icf-document-hospital'
import { PERMISSIONS } from '../../../../../../../../lib/permission'

export const useIcfDocumentList = () => {
  const navigate = useNavigate()
  const { selectedTrial } = useSelectedTrial()
  const { uid: trialUid } = selectedTrial
  const { selectedTrialHospitalUid } = useIcfDocumentHospital()
  const { hasPermission } = usePermission()
  const toast = useMirohaToast()

  const hasPermissionReAgreement = hasPermission(PERMISSIONS.Explanation_Edit)

  const { data: icfDocumentList, error: fetchError } = useApiClientFetch({
    path: '/trials/{trial_uid}/trial_hospitals/{trial_hospital_uid}/icf_documents',
    params: {
      paths: {
        trial_uid: trialUid,
        trial_hospital_uid: selectedTrialHospitalUid!,
      },
    },
    onSuccess: data => {
      const hasRequiredReAgreementConfirmationDoc = data.some(
        doc => doc.isRequiredReAgreementConfirmation,
      )

      if (hasPermissionReAgreement && hasRequiredReAgreementConfirmationDoc) {
        toast({
          status: 'info',
          title: '再同意確認が必要な文書があります。',
          description:
            '再同意確認ボタンから、改版文書の再同意実施有無を設定してください。',
          duration: 15000,
          isClosable: true,
        })
      }
    },
    onError: e => {
      toast({ status: 'error', title: e.data.message })
    },
    shouldCancel: selectedTrialHospitalUid === undefined,
    revalidateOnFocus: false,
  })

  const shouldShowReAgreementConfirmButton = useMemo(() => {
    if (!icfDocumentList) return false
    if (!hasPermissionReAgreement) return false
    return icfDocumentList.some(doc => doc.isRequiredReAgreementConfirmation)
  }, [hasPermissionReAgreement, icfDocumentList])

  const navigateToAdd = useCallback(() => {
    navigate(
      getAddIcfDocumentRoute({
        trialUid,
        trialHospitalUid: hasPermission(
          PERMISSIONS.Icfdocument_SelectTrialHospital,
        )
          ? selectedTrialHospitalUid
          : undefined,
      }),
    )
  }, [navigate, trialUid, hasPermission, selectedTrialHospitalUid])

  const navigateToEdit = useCallback(
    ({
      icfDocumentUid,
      latestRevisionUid: _,
    }: {
      icfDocumentUid: string
      latestRevisionUid: string
    }) => {
      navigate(
        getEditIcfDocumentRoute({
          trialUid,
          icfDocumentUid,
          trialHospitalUid: hasPermission(
            PERMISSIONS.Icfdocument_SelectTrialHospital,
          )
            ? selectedTrialHospitalUid
            : undefined,
        }),
      )
    },
    [navigate, trialUid, hasPermission, selectedTrialHospitalUid],
  )

  return {
    icfDocumentList: icfDocumentList,
    fetchError,
    selectedTrialHospitalUid,
    shouldShowReAgreementConfirmButton,
    navigateToAdd,
    navigateToEdit,
  }
}
